/**
 * The subscribe flow's data layer — turns a browser `PushSubscription` into a
 * durable `public.push_subscriptions` row.
 *
 * Portal-local data (ADR-0005): written directly through Kong -> PostgREST
 * under RLS, using the customer's own JWT. There is NO BFF hop here and no
 * hand-written ownership check in front of it — `push_subscriptions_own`
 * (`portal_user_id = auth.uid() and portal_user_is_activated(auth.uid())`,
 * gating `using` AND `with check`, see `db/migrations/20260730150000_…`) is
 * the entire authorization story. This module never re-implements it.
 *
 * Follows `notifications.ts` / `portalUsers.ts`'s shape: every function takes
 * the `SupabaseClient` as a parameter rather than reaching for
 * `getSupabaseClient()` internally, so a test can inject a stub with no
 * `__setSupabaseClientForTests` and no React tree. Every function returns
 * `{ data | error }` (or `{ error }`) and never throws.
 *
 * ---------------------------------------------------------------------------
 * One row per device — enforced at the database, not by hope.
 *
 * `endpoint` is UNIQUE. Persisting a subscription is therefore always an
 * UPSERT keyed on `endpoint` (never a blind insert): calling
 * `upsertPushSubscription` twice with the same subscription updates
 * `last_seen_at` on the existing row rather than creating a second one.
 *
 * Browsers rotate endpoints silently, so "the stored row is still valid" is
 * not a safe assumption on app start. `reconcilePushSubscription` is what a
 * caller invokes at that moment: it upserts the current subscription and,
 * when the caller knows the endpoint that used to be stored for this device,
 * removes that stale row.
 *
 * ---------------------------------------------------------------------------
 * Personal data.
 *
 * `endpoint`, `p256dh` and `auth_key` identify a customer's device
 * (CLAUDE.md, ADR-0006). NEVER log them, never put them in an error message,
 * never let them cross the integration boundary. The error handling below
 * only ever surfaces `PostgrestError.message`, never the row or the request
 * payload.
 */
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';

const TABLE = 'push_subscriptions';

/**
 * The minimal shape this module needs from the browser's `PushSubscription`.
 * Kept as our own narrow interface, separate from the DOM type, so a test can
 * supply a plain object instead of constructing a real `PushSubscription` —
 * mirrors `PushEnvironment` in `docs/handoff/pushState.ts` for the same
 * reason. A real `PushSubscription` satisfies this structurally.
 */
export interface BrowserPushSubscription {
  endpoint: string;
  getKey(name: 'p256dh' | 'auth'): ArrayBuffer | null;
}

/**
 * The raw `public.push_subscriptions` row shape, as PostgREST returns it.
 * Already English at the column level — `auth_key` is the one place the
 * column name is not simply the camelCase of the domain field, mapped below.
 */
export interface PushSubscriptionRow {
  id: string;
  portal_user_id: string;
  endpoint: string;
  p256dh: string;
  auth_key: string;
  user_agent: string | null;
  created_at: string;
  last_seen_at: string;
}

const SELECT_COLUMNS = 'id,portal_user_id,endpoint,p256dh,auth_key,user_agent,created_at,last_seen_at';

/** The domain type every caller above this module should use. */
export interface PushDevice {
  id: string;
  portalUserId: string;
  /** PERSONAL DATA — identifies a device. Never log it. */
  endpoint: string;
  /** PERSONAL DATA. Never log it. */
  p256dh: string;
  /** PERSONAL DATA. Never log it. */
  authKey: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
}

function mapRow(row: PushSubscriptionRow): PushDevice {
  return {
    id: row.id,
    portalUserId: row.portal_user_id,
    endpoint: row.endpoint,
    p256dh: row.p256dh,
    authKey: row.auth_key,
    userAgent: row.user_agent,
    createdAt: row.created_at,
    lastSeenAt: row.last_seen_at,
  };
}

/** Never includes a request payload or row value — see module doc. */
function messageOf(error: PostgrestError | Error | unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return String(error);
}

export interface PushSubscriptionResult {
  data: PushDevice | null;
  /** Present when the write failed; the caller decides how to degrade. */
  error: string | null;
}

/**
 * Persists a `PushSubscription` as one row, upserted on the unique
 * `endpoint`. Running this twice for the same subscription updates
 * `last_seen_at` (and refreshes the keys, in case the browser reused the
 * endpoint with new keys) on the SAME row rather than creating a second one —
 * `created_at` is deliberately left out of the payload so the upsert never
 * touches it on conflict.
 *
 * Returns a handled error, never throws, if either encryption key is
 * missing — that should not happen for a subscription that came out of a
 * successful `pushManager.subscribe()`, but a null from `getKey` is a real
 * possibility per the DOM types and must not reach `btoa` as `null`.
 */
export async function upsertPushSubscription(
  client: SupabaseClient,
  portalUserId: string,
  subscription: BrowserPushSubscription,
  userAgent: string | null,
): Promise<PushSubscriptionResult> {
  try {
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');

    if (!p256dhKey || !authKey) {
      return { data: null, error: 'push subscription is missing an encryption key' };
    }

    const row = {
      portal_user_id: portalUserId,
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64Url(p256dhKey),
      auth_key: arrayBufferToBase64Url(authKey),
      user_agent: userAgent,
      last_seen_at: new Date().toISOString(),
    };

    const { data, error } = await client
      .from(TABLE)
      .upsert(row, { onConflict: 'endpoint' })
      .select(SELECT_COLUMNS)
      .maybeSingle();

    if (error) {
      return { data: null, error: error.message };
    }

    const result = data as unknown as PushSubscriptionRow | null;
    return { data: result ? mapRow(result) : null, error: null };
  } catch (err) {
    return { data: null, error: messageOf(err) };
  }
}

/**
 * Reconciles the stored row with the browser's current subscription — the
 * thing a caller invokes on every app start, since browsers rotate endpoints
 * silently and nothing tells the app.
 *
 * Always upserts the current subscription first (never leaves the device
 * un-registered because a delete failed). Only when `previousEndpoint` is
 * given AND differs from the current one does it remove the stale row —
 * `previousEndpoint` is whatever the caller last knew was stored for THIS
 * device (e.g. persisted locally), never a lookup across the customer's other
 * devices.
 *
 * If the upsert itself fails, the stale row is left in place rather than
 * removed — deleting the last known-good row before its replacement is
 * confirmed durable would leave the device with nothing.
 */
export async function reconcilePushSubscription(
  client: SupabaseClient,
  portalUserId: string,
  subscription: BrowserPushSubscription,
  previousEndpoint: string | null,
  userAgent: string | null,
): Promise<PushSubscriptionResult> {
  const result = await upsertPushSubscription(client, portalUserId, subscription, userAgent);

  if (result.error) {
    return result;
  }

  if (previousEndpoint && previousEndpoint !== subscription.endpoint) {
    await removePushSubscription(client, previousEndpoint);
  }

  return result;
}

export interface RemovePushSubscriptionResult {
  /** Present when the delete failed; the caller decides how to degrade. */
  error: string | null;
}

/**
 * Removes a subscription row by `endpoint` — for unsubscribe, for pruning a
 * row the caller already knows is dead (e.g. after reconciling), and as the
 * second half of `reconcilePushSubscription`.
 *
 * RLS (`push_subscriptions_own`) additionally guarantees the row must belong
 * to the caller; this is not itself the authorization check. Deleting an
 * endpoint that does not exist, or does not belong to the caller, is not an
 * error — PostgREST reports zero rows affected, not a failure.
 *
 * ⛔ AN RPC, NOT `.delete().eq('endpoint', …)`. supabase-js renders a filter
 *    into the QUERY STRING, and the edge proxy logs raw request URIs — so the
 *    table form wrote a customer's Web Push endpoint into the access log on
 *    every call. Measured before this changed: 32 real FCM endpoints in slot
 *    01's edge log. An RPC argument travels in the POST body, which is not
 *    logged. See `db/migrations/20260731190000_…`.
 */
export async function removePushSubscription(
  client: SupabaseClient,
  endpoint: string,
): Promise<RemovePushSubscriptionResult> {
  try {
    const { error } = await client.rpc('portal_delete_push_subscription', {
      p_endpoint: endpoint,
    });

    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    return { error: messageOf(err) };
  }
}

/**
 * Does the row for `endpoint` belong to the account that is logged in RIGHT NOW?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * The question this answers is not "is this device subscribed" — the browser
 * already answers that, and its answer is a DEVICE fact. On a shared phone the
 * browser keeps saying yes after the previous customer logs out, so deciding
 * from it alone told the next customer "notifications are on" while nothing
 * addressed to them was ever delivered.
 *
 * RLS is what makes this a one-line query rather than a comparison: the
 * `push_subscriptions_own` policy already restricts every SELECT to
 * `portal_user_id = auth.uid()`, so a row coming back IS proof of ownership,
 * and the previous customer's row is invisible here rather than merely
 * different. Nothing in this function re-implements that check.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `null` means UNDETERMINED, never false: a network failure must not be
 * rendered as "this device belongs to somebody else". Callers treat null as
 * "assume it is theirs" (see `derivePushState`).
 *
 * ⛔ AN RPC, NOT `.select().eq('endpoint', …)` — same reason as
 *    `removePushSubscription` above: a filter becomes a query string, and the
 *    edge proxy logs raw URIs. The RPC is `stable`, so PostgREST would also
 *    accept it over GET, which would put the endpoint straight back into the
 *    URL. `.rpc()` uses POST by default; do not pass `{ get: true }`.
 */
export async function isSubscriptionMine(
  client: SupabaseClient,
  endpoint: string,
): Promise<boolean | null> {
  try {
    const { data, error } = await client.rpc('portal_push_subscription_is_mine', {
      p_endpoint: endpoint,
    });

    // Never surfaces `endpoint` — personal data, and this is an error path.
    if (error) {
      return null;
    }

    return data === true;
  } catch {
    return null;
  }
}

/**
 * Encodes raw key bytes as base64url (no padding) — the inverse of
 * `base64UrlToUint8Array`, used to store `getKey('p256dh')` / `getKey('auth')`
 * as text.
 */
function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decodes a base64url string (no padding, `-`/`_` in place of `+`/`/`) to raw
 * bytes — what `pushManager.subscribe({ applicationServerKey })` needs from
 * `VITE_VAPID_PUBLIC_KEY`.
 *
 * A pure, exported function on purpose: getting base64url subtly wrong (e.g.
 * forgetting the `-`/`_` swap, or mishandling missing padding) is a classic
 * silent-failure source — `subscribe()` fails with an opaque browser error
 * that gives no hint the application server key was malformed. Worth a direct
 * unit test against known vectors, independent of any browser API.
 */
export function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(paddingLength);

  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
